import { CurrencyEnum, PaymentSettings, PriceIntervalEnum } from "@finway-group/shared/lib/models"
import React from "react"
import { useTranslation } from "react-i18next"
import { Button, Card, Col, Modal, Row } from "antd"
import PaymentSettingsService, { PaymentProductUI } from "Shared/services/paymentSettings.service"
import { NotificationService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { use2LetterLocale } from "Shared/hooks/i18n.hooks"
import PriceLabel from "Components/priceLabel"
import { ArrowThinRightIcon, CardXLSuccessIcon, CubeIcon, LinesIcon, RocketIcon } from "Components/icons"
import { IconBackground } from "Components/icons/iconBackground"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

interface PaymentSubscriptionSwitchToAnnualModalInterface {
    subscriptionPlan: PaymentProductUI
    onUpgraded: (updatedPaymentSettings?: PaymentSettings) => void
    isShowing: boolean
    onCancel: () => void
}

enum PaymentSubscriptionSwitchToAnnualUpdateStep {
    SELECT,
    CONFIM,
    FINISH,
}

const PaymentSubscriptionSwitchToAnnualModal: React.FC<PaymentSubscriptionSwitchToAnnualModalInterface> = ({ subscriptionPlan, onUpgraded, isShowing, onCancel }) => {
    const { t } = useTranslation()
    const locale = use2LetterLocale()
    const [isUpgrading, setIsUpgrading] = useStateIfMounted<boolean>(false)
    const [step, setStep] = useStateIfMounted(PaymentSubscriptionSwitchToAnnualUpdateStep.SELECT)

    const onHide = () => {
        setStep(PaymentSubscriptionSwitchToAnnualUpdateStep.SELECT)
        onCancel()
    }

    const onSwitchToAnnual = () => {
        setStep(PaymentSubscriptionSwitchToAnnualUpdateStep.CONFIM)
    }

    const onSwitchToAnnualConfirmation = () => {
        setIsUpgrading(true)
        PaymentSettingsService.switchSubscriptionBillingInterval()
            .then((updatedPaymentSettings) => {
                onUpgraded(updatedPaymentSettings)
                setStep(PaymentSubscriptionSwitchToAnnualUpdateStep.FINISH)
            })
            .catch((err) => NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error")))
            .finally(() => setIsUpgrading(false))
    }

    const renderPriceCard = (interval: PriceIntervalEnum) => {
        const price = subscriptionPlan.prices?.find((e: any) => e.interval === interval)
        return (
            <Card className="bg-gray-200 ">
                <Row>
                    <Col span={5}>
                        <IconBackground bg="bg-white">
                            <CubeIcon />
                        </IconBackground>
                    </Col>
                    <Col span={19}>
                        <Row justify="space-between">
                            <Col>
                                <span>{subscriptionPlan.metadata[`${locale}_name`] ?? subscriptionPlan.name}</span>
                            </Col>
                            <Col>
                                <span className="finway-green-text font-bold">
                                    <PriceLabel currency={price?.currency ?? CurrencyEnum.EUR} value={(price?.amount ?? 1) / 100} />
                                </span>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <span className="text-xs">
                                    {t("label:payment_settings.annual.up_to")}
                                    <span className="font-bold"> {subscriptionPlan.metadata?.units} </span>
                                    {t("label:payment_settings.annual.invoices_per_month")}
                                </span>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Card>
        )
    }

    const renderContent = () => {
        switch (step) {
            case PaymentSubscriptionSwitchToAnnualUpdateStep.SELECT: {
                return (
                    <>
                        <Row>
                            <Col span={11}>
                                <p>{t("label:payment_settings.annual.monthly")}</p>
                                {renderPriceCard(PriceIntervalEnum.MONTHLY)}
                            </Col>
                            <Col span={2} className="flex justify-center items-center my-auto h-full">
                                <ArrowThinRightIcon />
                            </Col>
                            <Col span={11}>
                                <p>{t("label:payment_settings.annual.annual")}</p>
                                {renderPriceCard(PriceIntervalEnum.YEARLY)}
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <Card className="py-10">
                                    <p className="finway-green-text font-bold text-xl">{t("label:payment_settings.annual.save_20_title")}</p>
                                    <p>{t("label:payment_settings.annual.save_20_text")}</p>
                                </Card>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <p className="mt-10">{t("label:payment_settings.annual.effective_info")}</p>
                            </Col>
                        </Row>
                    </>
                )
            }
            case PaymentSubscriptionSwitchToAnnualUpdateStep.CONFIM: {
                return (
                    <div className="flex flex-col items-center">
                        <p className="text-center text-xs">{t("confirm:payment_settings.annual.message.top")}</p>
                        <h2 className="text-center mt-20">{t("confirm:payment_settings.annual.message.bot")}</h2>
                    </div>
                )
            }
            case PaymentSubscriptionSwitchToAnnualUpdateStep.FINISH: {
                return (
                    <Row justify="space-around">
                        <Col className="flex flex-col items-center">
                            <div className="relative flex items-center my-20">
                                {/* TODO FA-22: use correct theme color classes for icons */}
                                <CardXLSuccessIcon className="absolute mx-auto inset-x-0 finway-primary-text" style={{ width: 230 }} />
                                <LinesIcon style={{ color: "#F3F3F3" }} />
                            </div>
                            <div className="flex flex-col items-center">
                                <h2 className="text-center my-20">{t("label:payment_settings.annual.thx")}</h2>
                                <p className="text-center">{t("label:payment_settings.annual.effective_done")}</p>
                            </div>
                        </Col>
                    </Row>
                )
            }
        }
    }

    const cancelButton = (
        <Button key="c" onClick={onHide}>
            {t("action:cancel")}
        </Button>
    )

    const renderFooter = () => {
        switch (step) {
            case PaymentSubscriptionSwitchToAnnualUpdateStep.SELECT:
                return [
                    cancelButton,
                    <Button key="s" loading={isUpgrading} onClick={onSwitchToAnnual} icon={<RocketIcon />} className="btn-highlight-green-active">
                        {t("action:payment_settings.annual_payment_change")}
                    </Button>,
                ]
            case PaymentSubscriptionSwitchToAnnualUpdateStep.CONFIM:
                return [
                    <Button key="c" loading={isUpgrading} onClick={onHide}>
                        {t("action:cancel")}
                    </Button>,
                    <Button key="s" loading={isUpgrading} onClick={onSwitchToAnnualConfirmation} icon={<RocketIcon />} className="btn-highlight-green-active">
                        {t("confirm:payment_settings.annual.confirm")}
                    </Button>,
                ]
            case PaymentSubscriptionSwitchToAnnualUpdateStep.FINISH:
                return [
                    <Button key="c" className="ant-btn-primary" onClick={onHide}>
                        {t("action:payment_settings.back_to_finway")}
                    </Button>,
                ]
        }
    }

    if (Boolean(subscriptionPlan.metadata.custom))
        return (
            <Modal title={t("label:payment_settings.annual.title")} width={600} visible={isShowing} maskClosable={false} onCancel={onHide} footer={cancelButton} destroyOnClose>
                {t("notification:payment_settings.annual_payment_not_allowed.message")}
            </Modal>
        )

    return (
        <Modal title={t("label:payment_settings.annual.title")} width={600} visible={isShowing} maskClosable={false} onCancel={onHide} footer={renderFooter()} destroyOnClose>
            {renderContent()}
        </Modal>
    )
}

export default PaymentSubscriptionSwitchToAnnualModal
